import {
  IconFile,
  IconFileTypeBmp,
  IconFileTypeCsv,
  IconFileTypeDoc,
  IconFileTypeDocx,
  IconFileTypeHtml,
  IconFileTypeJpg,
  IconFileTypePdf,
  IconFileTypePng,
  IconFileTypePpt,
  IconFileTypeSvg,
  IconFileTypeTxt,
  IconFileTypeXls,
  IconFileTypeXml,
  IconFileTypeZip,
  IconFileZip,
  IconMail,
  IconMovie,
  IconMusic,
  IconNotebook,
  IconPhoto,
} from '@tabler/icons-react'
import { has, isEmpty, isNil, keyBy, maxBy, uniq } from 'lodash'
import pluralize from 'pluralize'

import { NOT_SYNCED } from '@/reducers/organizations/documentSourceConnection.actions'

/**
 * A top-level node of the Manage files tree: a library or drive, or a Google Drive folder entry.
 * @typedef {Object} ManageFilesRoot
 * @property {string} driveId
 * @property {string|null} rootId - the folder id of a Google Drive folder entry, else null
 * @property {string} label
 * @property {string|null} lastSyncedAt
 */

/**
 * An API item plus where it sits. Browse items' `ancestorIds` run from the top of the shown
 * tree down to the parent (a folder root's id first); search matches carry their full chain.
 * @typedef {Object} ManageFilesItem
 * @property {string} drive_id
 * @property {string[]} ancestorIds
 */

/**
 * An unsaved checkbox click.
 * @typedef {Object} Change
 * @property {string} drive_id
 * @property {string} item_id
 * @property {boolean} blocked
 * @property {string[]} ancestorIds
 */

/**
 * Loaded pages, keyed by `itemKey` for items and by `rootKey` or the folder's `itemKey` for listings.
 * @typedef {Object} Cache
 * @property {Object<string, ManageFilesItem>} items
 * @property {Object<string, {keys: string[], nextCursor: string|null, loading: boolean, error: {kind: string, message: string|null}|null}>} listings
 * @property {Object<string, {name: string, web_url: string}>} parents
 */

/**
 * One node of a search result; `item` is null for an ancestor shown only as context.
 * @typedef {Object} SearchNode
 * @property {string} key
 * @property {string} label
 * @property {ManageFilesItem|null} item
 * @property {SearchNode[]} children
 */

export const PENDING_TOOLTIP = 'Added after the next nightly sync'

export const FILE_STATUS = {
  INDEXED: 'indexed',
  PENDING: 'pending',
  SKIPPED: 'skipped',
  FAILED: 'failed',
}

export const BLOCKED_REASON = 'blocked'

export const SKIP_REASON_LABELS = {
  unsupported_type: 'Unsupported file type',
  too_large: 'Too large to index',
  shortcut: 'Shortcut to another file',
}

// The quarantine reasons agent-gpt's ingestion records on a failed file.
export const FAIL_REASON_LABELS = {
  ingest_error: "Couldn't read this file",
  no_content: 'No readable text',
  chunk_cap: 'Too much text to index',
  crash: 'Reading this file stopped unexpectedly',
  memory_pressure: 'Too big to read',
  conversion_killed: 'Too big to read',
}

// Color families for file icons; each has a helpers.scss class, `file-icon-<tone>`.
export const FILE_TONE = { PDF: 'pdf', WORD: 'word', EXCEL: 'excel', POWERPOINT: 'powerpoint' }

const FILE_TYPE_BY_EXTENSION = {
  pdf: { icon: IconFileTypePdf, tone: FILE_TONE.PDF },
  doc: { icon: IconFileTypeDoc, tone: FILE_TONE.WORD },
  dot: { icon: IconFileTypeDoc, tone: FILE_TONE.WORD },
  rtf: { icon: IconFileTypeDoc, tone: FILE_TONE.WORD },
  odt: { icon: IconFileTypeDoc, tone: FILE_TONE.WORD },
  docx: { icon: IconFileTypeDocx, tone: FILE_TONE.WORD },
  docm: { icon: IconFileTypeDocx, tone: FILE_TONE.WORD },
  dotx: { icon: IconFileTypeDocx, tone: FILE_TONE.WORD },
  dotm: { icon: IconFileTypeDocx, tone: FILE_TONE.WORD },
  xls: { icon: IconFileTypeXls, tone: FILE_TONE.EXCEL },
  xlsx: { icon: IconFileTypeXls, tone: FILE_TONE.EXCEL },
  xlsm: { icon: IconFileTypeXls, tone: FILE_TONE.EXCEL },
  xlsb: { icon: IconFileTypeXls, tone: FILE_TONE.EXCEL },
  ods: { icon: IconFileTypeXls, tone: FILE_TONE.EXCEL },
  csv: { icon: IconFileTypeCsv, tone: FILE_TONE.EXCEL },
  ppt: { icon: IconFileTypePpt, tone: FILE_TONE.POWERPOINT },
  pptx: { icon: IconFileTypePpt, tone: FILE_TONE.POWERPOINT },
  pptm: { icon: IconFileTypePpt, tone: FILE_TONE.POWERPOINT },
  pps: { icon: IconFileTypePpt, tone: FILE_TONE.POWERPOINT },
  ppsx: { icon: IconFileTypePpt, tone: FILE_TONE.POWERPOINT },
  odp: { icon: IconFileTypePpt, tone: FILE_TONE.POWERPOINT },
  png: { icon: IconFileTypePng, tone: null },
  jpg: { icon: IconFileTypeJpg, tone: null },
  jpeg: { icon: IconFileTypeJpg, tone: null },
  svg: { icon: IconFileTypeSvg, tone: null },
  bmp: { icon: IconFileTypeBmp, tone: null },
  gif: { icon: IconPhoto, tone: null },
  tif: { icon: IconPhoto, tone: null },
  tiff: { icon: IconPhoto, tone: null },
  webp: { icon: IconPhoto, tone: null },
  heic: { icon: IconPhoto, tone: null },
  txt: { icon: IconFileTypeTxt, tone: null },
  md: { icon: IconFileTypeTxt, tone: null },
  html: { icon: IconFileTypeHtml, tone: null },
  htm: { icon: IconFileTypeHtml, tone: null },
  aspx: { icon: IconFileTypeHtml, tone: null },
  xml: { icon: IconFileTypeXml, tone: null },
  zip: { icon: IconFileTypeZip, tone: null },
  '7z': { icon: IconFileZip, tone: null },
  rar: { icon: IconFileZip, tone: null },
  gz: { icon: IconFileZip, tone: null },
  msg: { icon: IconMail, tone: null },
  eml: { icon: IconMail, tone: null },
  one: { icon: IconNotebook, tone: null },
  mp4: { icon: IconMovie, tone: null },
  mov: { icon: IconMovie, tone: null },
  mp3: { icon: IconMusic, tone: null },
  wav: { icon: IconMusic, tone: null },
  m4a: { icon: IconMusic, tone: null },
}

const PLAIN_FILE = { icon: IconFile, tone: null }

// The extension picks the icon and its color; a name without one (a native Google Doc) is a plain file.
export const fileTypeOf = (name) => {
  const dot = name.lastIndexOf('.')
  const extension = dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
  return has(FILE_TYPE_BY_EXTENSION, [extension]) ? FILE_TYPE_BY_EXTENSION[extension] : PLAIN_FILE
}

const BLOCKED_STATUS = { label: 'Blocked', variant: 'critical', tooltip: null }
export const PENDING_STATUS = { label: 'Pending', variant: 'warning', tooltip: PENDING_TOOLTIP }
const INDEXED_STATUS = { label: 'Indexed', variant: 'success', tooltip: null }
const SKIPPED_STATUS = { label: 'Skipped', variant: null }
const FAILED_STATUS = { label: 'Failed', variant: 'critical' }

export const EMPTY_CACHE = { items: {}, listings: {}, parents: {} }

const EMPTY_LISTING = { keys: [], nextCursor: null, loading: false, error: null }

export const itemKey = (driveId, itemId) => `${driveId}:${itemId}`

export const rootKey = (root) => `root:${root.driveId}:${root.rootId ?? ''}`

const listingOf = (cache, parentKey) => cache.listings[parentKey] ?? EMPTY_LISTING

export const markListing = (cache, parentKey, { loading = false, error = null }) => ({
  ...cache,
  listings: { ...cache.listings, [parentKey]: { ...listingOf(cache, parentKey), loading, error } },
})

// Keys already listed are skipped: a renamed anchor between pages can repeat a row.
// `replace` restarts a listing whose cursor went stale, keeping no rows from it.
export const appendPage = (cache, { parentKey, driveId, ancestorIds, replace = false }, page) => {
  const stamped = page.items.map((item) => ({ ...item, drive_id: driveId, ancestorIds }))
  const keyOf = (item) => itemKey(driveId, item.item_id)
  const listedKeys = replace ? [] : listingOf(cache, parentKey).keys

  return {
    items: { ...cache.items, ...keyBy(stamped, keyOf) },
    listings: {
      ...cache.listings,
      [parentKey]: {
        keys: uniq([...listedKeys, ...stamped.map(keyOf)]),
        nextCursor: page.next_cursor,
        loading: false,
        error: null,
      },
    },
    parents: isNil(page.parent) ? cache.parents : { ...cache.parents, [parentKey]: page.parent },
  }
}

export const completeChildrenOf = (cache) => (item) => {
  const listing = cache.listings[itemKey(item.drive_id, item.item_id)]
  if (isNil(listing) || listing.loading || !isNil(listing.error) || !isNil(listing.nextCursor)) {
    return null
  }

  return listing.keys.map((key) => cache.items[key])
}

const isOwnChange = (change, item) =>
  change.drive_id === item.drive_id && change.item_id === item.item_id

const isAncestorChange = (change, item) =>
  change.drive_id === item.drive_id && item.ancestorIds.includes(change.item_id)

export const isBeneath = (change, folder) =>
  change.drive_id === folder.drive_id && change.ancestorIds.includes(folder.item_id)

// An ancestor unblock voids the saved rows beneath it, the item's own included.
const ownBlockedOf = (item, changes, ancestorChanges) => {
  const ownChange = changes.find((change) => isOwnChange(change, item))
  if (!isNil(ownChange)) {
    return ownChange.blocked
  }
  if (ancestorChanges.some((change) => !change.blocked)) {
    return false
  }

  return item.blocked
}

// Mirrors agent-gpt's save: unblocks apply first and clear block rows beneath them, then blocks.
export const effectiveBlockState = (item, changes) => {
  const ancestorChanges = changes.filter((change) => isAncestorChange(change, item))
  const blockedByAncestor = isEmpty(ancestorChanges)
    ? item.blocked_by_ancestor
    : ancestorChanges.some((change) => change.blocked)

  return {
    blocked: blockedByAncestor || ownBlockedOf(item, changes, ancestorChanges),
    blockedByAncestor,
  }
}

// A complete listing answers from its children; otherwise the saved flag stands
// until an unblock on the folder or above clears the rows it counted.
export const isIndeterminate = (item, changes, childrenOf) => {
  if (!item.is_folder || effectiveBlockState(item, changes).blocked) {
    return false
  }
  if (changes.some((change) => change.blocked && isBeneath(change, item))) {
    return true
  }

  const children = childrenOf(item)
  if (!isNil(children)) {
    return children.some(
      (child) =>
        effectiveBlockState(child, changes).blocked || isIndeterminate(child, changes, childrenOf)
    )
  }

  const unblockedHere = changes.some(
    (change) => !change.blocked && (isOwnChange(change, item) || isAncestorChange(change, item))
  )
  return !unblockedHere && item.has_blocked_descendants === true
}

export const toggleTarget = (item, changes, childrenOf) =>
  !effectiveBlockState(item, changes).blocked && !isIndeterminate(item, changes, childrenOf)

// A click replaces the item's own change and every change beneath it, as agent-gpt's
// save would. An unblock of an indeterminate folder still counts: it means "include everything".
export const recordChange = (changes, item, blocked, childrenOf) => {
  const others = changes.filter((change) => !isOwnChange(change, item) && !isBeneath(change, item))
  const baseline = effectiveBlockState(item, others)
  // An ancestor's block decides what shows, so a change here would only light up Update.
  if (baseline.blockedByAncestor) {
    return others
  }

  const includesEverything = !blocked && isIndeterminate(item, others, childrenOf)
  if (blocked === baseline.blocked && !includesEverything) {
    return others
  }

  return [
    ...others,
    { drive_id: item.drive_id, item_id: item.item_id, blocked, ancestorIds: item.ancestorIds },
  ]
}

export const changesPayload = (changes) =>
  changes.map(({ drive_id, item_id, blocked }) => ({ drive_id, item_id, blocked }))

const MAX_CHANGES_PER_SAVE = 1000
const UNSAVED_CHANGE = 'unsaved change'
const CHANGE_CAP_NOTE = 'Save at most 1,000 changes at a time.'

// The API rejects a bigger save with an error the user can't act on, so Update stays disabled.
// The count stays in view so the user knows how many to undo.
export const footerOf = (changes) => {
  if (isEmpty(changes)) {
    return { note: null, canSave: false }
  }

  const count = changes.length
  const countNote = `${count.toLocaleString('en-US')} ${pluralize(UNSAVED_CHANGE, count)}`
  if (count > MAX_CHANGES_PER_SAVE) {
    return { note: `${countNote}. ${CHANGE_CAP_NOTE}`, canSave: false }
  }

  return { note: countNote, canSave: true }
}

export const statusOf = (item, state) => {
  if (item.is_folder) {
    return null
  }
  if (state.blocked) {
    return BLOCKED_STATUS
  }

  const reason = item.status_reason
  if (
    (item.status === FILE_STATUS.SKIPPED && reason === BLOCKED_REASON) ||
    item.status === FILE_STATUS.PENDING
  ) {
    return PENDING_STATUS
  }
  if (item.status === FILE_STATUS.INDEXED) {
    return INDEXED_STATUS
  }
  if (item.status === FILE_STATUS.SKIPPED) {
    return {
      ...SKIPPED_STATUS,
      tooltip: has(SKIP_REASON_LABELS, [reason]) ? SKIP_REASON_LABELS[reason] : reason,
    }
  }
  if (item.status === FILE_STATUS.FAILED) {
    return {
      ...FAILED_STATUS,
      tooltip: has(FAIL_REASON_LABELS, [reason]) ? FAIL_REASON_LABELS[reason] : reason,
    }
  }

  return null
}

// Each path is the steps from just below the root down to one match; grouping paths by
// their first step gives one node per item key, so a matched ancestor keeps its children.
const nodesOf = (paths) =>
  uniq(paths.map((path) => path[0].key)).map((key) => {
    const throughKey = paths.filter((path) => path[0].key === key)
    const matchStep = throughKey.map((path) => path[0]).find((step) => !isNil(step.item))

    return {
      key,
      label: matchStep?.label ?? throughKey[0][0].label,
      item: matchStep?.item ?? null,
      children: nodesOf(throughKey.map((path) => path.slice(1)).filter((rest) => !isEmpty(rest))),
    }
  })

// A drive root (null rootId) is in no chain, so it sits at -1: above every folder root,
// showing the match's whole chain.
const rootIndexOf = (match, root) =>
  match.ancestors.findIndex((ancestor) => ancestor.item_id === root.rootId)

const searchPathOf = (match, root) => {
  const context = match.ancestors.slice(rootIndexOf(match, root) + 1).map((ancestor) => ({
    key: itemKey(match.drive_id, ancestor.item_id),
    label: ancestor.name,
  }))
  const item = { ...match, ancestorIds: match.ancestors.map((ancestor) => ancestor.item_id) }

  return [...context, { key: itemKey(match.drive_id, match.item_id), label: match.name, item }]
}

const claimsMatch = (root, match) =>
  root.driveId === match.drive_id && (isNil(root.rootId) || rootIndexOf(match, root) >= 0)

// Overlapping roots in one drive: the deepest claiming root owns a match, so a folder root
// beats the drive root. A match no root claims is dropped.
export const searchTreeOf = (roots, matches) => {
  const ownerOf = (match) =>
    maxBy(
      roots.filter((root) => claimsMatch(root, match)),
      (root) => rootIndexOf(match, root)
    )

  return Object.fromEntries(
    roots.map((root) => {
      const owned = matches.filter((match) => ownerOf(match) === root)
      return [rootKey(root), nodesOf(owned.map((match) => searchPathOf(match, root)))]
    })
  )
}

/**
 * One row of the Manage files tree, before the modal decorates it into cells.
 * @typedef {Object} RowModel
 * @property {string} key
 * @property {string} label
 * @property {'root'|'item'|'context'|'notice'} kind
 * @property {ManageFilesItem|null} item
 * @property {ManageFilesRoot} root
 * @property {string|null} webUrl
 * @property {string|null} lastModified
 * @property {{label: string, variant: string|null, tooltip: string|null}|null} status - a statusOf result
 * @property {string|null} notice - a notice row's copy, or why a root can't expand yet
 * @property {boolean} removalPending
 * @property {boolean} selected
 * @property {boolean} indeterminate
 * @property {boolean} checkboxDisabled
 * @property {boolean} hideCheckbox
 * @property {boolean} hasChildren
 * @property {RowModel[]} childRows
 * @property {boolean} childRowsLoading
 * @property {string|null} nextCursor - non-null means Show more; null while the listing loads
 * @property {{parentKey: string, driveId: string, parentId: string|null, ancestorIds: string[]}|null} loadTarget
 */

export const ROW_KIND = { ROOT: 'root', ITEM: 'item', CONTEXT: 'context', NOTICE: 'notice' }

export const NOT_SYNCED_NOTICE = 'Files appear here after the next sync.'
const LOAD_FAILED_NOTICE = "Couldn't load these files."
const EMPTY_LISTING_NOTICE = 'No files here yet.'

const ROW_DEFAULTS = {
  item: null,
  webUrl: null,
  lastModified: null,
  status: null,
  notice: null,
  removalPending: false,
  selected: false,
  indeterminate: false,
  checkboxDisabled: false,
  hideCheckbox: false,
  hasChildren: false,
  childRows: [],
  childRowsLoading: false,
  nextCursor: null,
  loadTarget: null,
}

// The server's own copy, such as a folder that is gone, says more than the generic notice.
const noticeOf = (listing) => {
  if (!isNil(listing.error)) {
    return listing.error.kind === NOT_SYNCED
      ? NOT_SYNCED_NOTICE
      : listing.error.message ?? LOAD_FAILED_NOTICE
  }
  if (!listing.loading && isEmpty(listing.keys)) {
    return EMPTY_LISTING_NOTICE
  }

  return null
}

const itemRowOf = (item, { root, changes, childrenOf }) => {
  const key = itemKey(item.drive_id, item.item_id)
  const state = effectiveBlockState(item, changes)
  const indeterminate = isIndeterminate(item, changes, childrenOf)

  return {
    ...ROW_DEFAULTS,
    key,
    label: item.name,
    kind: ROW_KIND.ITEM,
    item,
    root,
    webUrl: item.web_url,
    lastModified: item.last_modified,
    status: statusOf(item, state),
    selected: !state.blocked && !indeterminate,
    indeterminate,
    checkboxDisabled: state.blockedByAncestor,
    hasChildren: item.is_folder && item.has_children === true,
    loadTarget: item.is_folder
      ? {
          parentKey: key,
          driveId: item.drive_id,
          parentId: item.item_id,
          ancestorIds: [...item.ancestorIds, item.item_id],
        }
      : null,
  }
}

// A row's children are its listing's items, each with its own children, then a notice
// when the listing failed or came back empty. A listing never fetched leaves none.
const withChildren = (row, context) => {
  const listing = isNil(row.loadTarget) ? null : context.cache.listings[row.loadTarget.parentKey]
  if (isNil(listing)) {
    return row
  }

  const itemRows = listing.keys.map((key) =>
    withChildren(itemRowOf(context.cache.items[key], context), context)
  )
  const notice = noticeOf(listing)
  const noticeRows = isNil(notice)
    ? []
    : [
        {
          ...ROW_DEFAULTS,
          key: `${row.loadTarget.parentKey}:notice`,
          label: notice,
          kind: ROW_KIND.NOTICE,
          root: context.root,
          notice,
          hideCheckbox: true,
        },
      ]

  return {
    ...row,
    childRows: [...itemRows, ...noticeRows],
    childRowsLoading: listing.loading,
    nextCursor: listing.loading ? null : listing.nextCursor,
  }
}

// A root missing from `drives` has no manifest yet, so it is treated as not synced.
const isRootSynced = (root, drives) =>
  drives.find((drive) => drive.drive_id === root.driveId)?.synced === true

const rootRowOf = (root, { drives, cache }) => {
  const parentKey = rootKey(root)
  const drive = drives.find((entry) => entry.drive_id === root.driveId)
  const synced = isRootSynced(root, drives)
  const parent = cache.parents[parentKey]

  return {
    ...ROW_DEFAULTS,
    key: parentKey,
    label: parent?.name ?? root.label,
    kind: ROW_KIND.ROOT,
    root,
    webUrl: parent?.web_url ?? null,
    notice: synced ? null : NOT_SYNCED_NOTICE,
    removalPending: drive?.removal_pending === true,
    hideCheckbox: true,
    hasChildren: synced,
    loadTarget: {
      parentKey,
      driveId: root.driveId,
      parentId: root.rootId,
      ancestorIds: isNil(root.rootId) ? [] : [root.rootId],
    },
  }
}

export const browseRowsOf = ({ roots, drives, cache, changes }) => {
  const childrenOf = completeChildrenOf(cache)

  return roots.map((root) =>
    withChildren(rootRowOf(root, { drives, cache }), { root, cache, changes, childrenOf })
  )
}

export const syncedRootsOf = (roots, drives) => roots.filter((root) => isRootSynced(root, drives))

// A stamped drive is offered before its first sync finishes, so `drives` may confirm what the
// sync row has not.
export const offersManageFiles = (roots, drives = []) =>
  roots.some((root) => !isNil(root.lastSyncedAt) || isRootSynced(root, drives))

// Search rows never lazy-load, so a matched folder shows only the matches beneath it.
const searchRowOf = (node, context) => {
  const row = isNil(node.item)
    ? {
        ...ROW_DEFAULTS,
        key: node.key,
        label: node.label,
        kind: ROW_KIND.CONTEXT,
        root: context.root,
        hideCheckbox: true,
      }
    : { ...itemRowOf(node.item, context), hasChildren: false, loadTarget: null }

  return { ...row, childRows: node.children.map((child) => searchRowOf(child, context)) }
}

export const searchRowsOf = ({ roots, drives, searchTree, changes, cache }) => {
  const childrenOf = completeChildrenOf(cache)

  return syncedRootsOf(roots, drives)
    .filter((root) => !isEmpty(searchTree[rootKey(root)]))
    .map((root) => ({
      ...rootRowOf(root, { drives, cache }),
      hasChildren: false,
      loadTarget: null,
      childRows: searchTree[rootKey(root)].map((node) =>
        searchRowOf(node, { root, changes, childrenOf })
      ),
    }))
}
